import { useMutation } from '@apollo/client/react';
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Segmented,
  Switch,
} from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { filter, keys } from 'lodash';
import { evaluate } from 'mathjs';
import moment from 'moment';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../AppContext';
import { InfoIconDark } from '../../../../../../assets/svg';
import {
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
  BREAKPOINTS,
  DATE_ARITHMETIC_FUNCTIONS,
  DATE_FUNCTION_TYPES,
  DATE_FUNCTIONS,
  DEFAULTDATEFORMAT,
  FIELD_TYPES,
  FIELD_TYPES_LABELS,
  FORMULA_EXAMPLES,
  INVALID_FORMULA_MESSAGE,
  MATHEMATICAL_FUNCTIONS,
  OPERATORS,
  OPERATORS_WITHOUT_BRACKETS,
  REGEX,
  REGISTER_CALCULATION_TYPES,
  TIME_24_HOUR_FORMAT,
  TIME_FUNCTION_TYPES,
  TIME_FUNCTIONS,
} from '../../../../../../common/constants';
import useRouter from '../../../../../../common/useRouter';
import {
  extractMathFunctionParams,
  formValidatorRules,
  regexPatterns,
  titleCase,
} from '../../../../../../common/utils';
import CanPerform from '../../../../../../components/CanPerform';
import CommonPopover from '../../../../../../components/CommonPopover';
import CommonSelect from '../../../../../../components/CommonSelect';
import RouterPrompt from '../../../../../../components/RouterPrompt';
import {
  CREATE_REGISTER_FORM_FIELD,
  UPDATE_REGISTER_FORM_FIELD,
} from '../../../../graphql/Mutation';
import CommonRegisterOptionUi from './CommonRegisterOptionUi';

const { required, characterWithoutWhiteSpace } = formValidatorRules;

export const CalculationLogicUi = ({
  isDisabled,
  numericFields,
  dateFields,
  timeFields,
  form,
  inputRef,
  showTypeView,
  setShowTypeView,
  formulaFieldValue,
  setFormulaFieldValue,
  mathematicalFunctions = MATHEMATICAL_FUNCTIONS,
}) => {
  const [currentExamples, setCurrentExamples] = useState({
    [FIELD_TYPES_LABELS.NUMERIC]: FORMULA_EXAMPLES?.['+'],
    [FIELD_TYPES_LABELS.DATE]: FORMULA_EXAMPLES.DAYSBETWEEN,
    [FIELD_TYPES_LABELS.TIME]: FORMULA_EXAMPLES.TIMESUBTRACTION,
  });

  const FUNCTION_PATTERNS = {
    DATE: new RegExp(`\\b(${DATE_FUNCTIONS.join('|')})\\([^)]*$`),
    TIME: new RegExp(`\\b(${TIME_FUNCTIONS.join('|')})\\([^)]*$`),
  };

  const extractExistingFields = (functionMatch) => {
    if (!functionMatch) return [];

    const insideParens = functionMatch?.split('(')?.[1] || '';
    return (
      insideParens?.split(',')?.filter((param) => param?.trim()?.length > 0) ||
      []
    );
  };

  const shouldAddCommaAfterField = (currentFormula, pattern) => {
    const functionMatch = currentFormula?.match(pattern);
    if (!functionMatch) return false;

    const existingFields = extractExistingFields(functionMatch?.[0]);
    return existingFields?.length === 0;
  };

  const formatFieldWithComma = (fieldTitle, currentFormula) => {
    const quotedField = `"${fieldTitle}"`;

    if (shouldAddCommaAfterField(currentFormula, FUNCTION_PATTERNS.DATE)) {
      return `${quotedField},`;
    }

    if (shouldAddCommaAfterField(currentFormula, FUNCTION_PATTERNS.TIME)) {
      return `${quotedField},`;
    }

    return quotedField;
  };

  const updateFormulaState = (newFormula) => {
    form?.setFieldsValue({ formula: newFormula });

    setFormulaFieldValue((prev) => ({
      ...prev,
      [showTypeView]: newFormula,
    }));
  };

  const handleButtonClick = (field) => {
    if (!field?.name) return;

    const fieldTitle = field?.name;
    const currentFormula = form.getFieldValue('formula') || '';

    const formattedField = formatFieldWithComma(fieldTitle, currentFormula);
    const newFormula = `${currentFormula}${formattedField}`;

    updateFormulaState(newFormula);
    inputRef?.current?.focus();
  };

  const hasFunctionSelected = () => {
    const currentFormula = form.getFieldValue('formula') || '';

    switch (showTypeView) {
      case FIELD_TYPES_LABELS.DATE:
        return DATE_FUNCTIONS?.some((func) => currentFormula?.includes(func));
      case FIELD_TYPES_LABELS.TIME:
        return TIME_FUNCTIONS?.some((func) => currentFormula?.includes(func));

      default:
        return true;
    }
  };

  const isDateTimeFormulaCompleted = () => {
    const currentFormula = form.getFieldValue('formula') || '';

    switch (showTypeView) {
      case FIELD_TYPES_LABELS.DATE:
        return DATE_FUNCTIONS?.some((func) => {
          const funcPattern = regexPatterns?.functionWithParams(func);
          return funcPattern?.test(currentFormula);
        });
      case FIELD_TYPES_LABELS.TIME:
        return TIME_FUNCTIONS?.some((func) => {
          const funcPattern = regexPatterns?.functionWithParams(func);
          return funcPattern?.test(currentFormula);
        });
      default:
        return false;
    }
  };

  const handleOperatorClick = (operator) => {
    const currentFormula = form.getFieldValue('formula') || '';
    let newOperator = operator;

    if (OPERATORS_WITHOUT_BRACKETS?.includes(operator)) {
      newOperator = ` ${operator} `;
    } else if (
      mathematicalFunctions?.includes(operator) ||
      DATE_FUNCTIONS?.includes(operator) ||
      TIME_FUNCTIONS?.includes(operator)
    ) {
      newOperator = `${operator}(`;
    }

    const newFormula = currentFormula + newOperator;
    form?.setFieldsValue({ formula: newFormula });

    setFormulaFieldValue((prev) => ({
      ...prev,
      [showTypeView]: newFormula,
    }));

    const formulaKey = OPERATORS?.includes(operator)
      ? operator
      : operator?.toUpperCase();
    if (FORMULA_EXAMPLES?.[formulaKey]) {
      setCurrentExamples((prev) => ({
        ...prev,
        [showTypeView]: FORMULA_EXAMPLES?.[formulaKey],
      }));
    }

    inputRef?.current?.focus();
  };

  return (
    <>
      <Row gutter={[20, 20]}>
        <Col span={24}>
          <Segmented
            id="calculation-type-segment"
            className="calculation-type-segment mb-10"
            options={[
              {
                label: FIELD_TYPES_LABELS.NUMERIC,
                value: FIELD_TYPES_LABELS.NUMERIC,
              },
              {
                label: FIELD_TYPES_LABELS.DATE,
                value: FIELD_TYPES_LABELS.DATE,
              },
              {
                label: FIELD_TYPES_LABELS.TIME,
                value: FIELD_TYPES_LABELS.TIME,
              },
            ]}
            block
            value={showTypeView}
            onChange={(value) => {
              const currentFormula = form.getFieldValue('formula') || '';
              setFormulaFieldValue((prev) => ({
                ...prev,
                [showTypeView]: currentFormula,
              }));

              setShowTypeView(value);
              form?.setFieldsValue({
                formula: formulaFieldValue?.[value] || '',
              });
            }}
            disabled={isDisabled}
          />
          <Form.Item
            name="formula"
            label="Formula"
            className="numeric-input"
            rules={[
              () => ({
                validator(_, value) {
                  if (!value) {
                    return Promise.reject(new Error('This field is required!'));
                  }
                  if (value?.length > 1000) {
                    return Promise.reject(
                      new Error('Formula cannot be more than 1000 characters'),
                    );
                  }
                  try {
                    let formulaToValidate = value;

                    if (value?.includes(DATE_FUNCTION_TYPES.DAYS_BETWEEN)) {
                      const daysBetweenRegex =
                        regexPatterns?.functionWithParamsCapture(
                          DATE_FUNCTION_TYPES.DAYS_BETWEEN,
                        );
                      const matches = [...value.matchAll(daysBetweenRegex)];

                      if (matches?.length > 0) {
                        const params = matches?.[0]?.[1]
                          ?.split(',')
                          ?.map((p) => p?.trim());
                        if (params?.length !== 2) {
                          return Promise.reject(
                            new Error(INVALID_FORMULA_MESSAGE),
                          );
                        }

                        const dateFieldParams = params?.filter((param) => {
                          const fieldName = param?.replace(/"/g, '');
                          return dateFields?.some(
                            (field) => field?.name === fieldName,
                          );
                        });

                        if (dateFieldParams?.length !== 2) {
                          return Promise.reject(
                            new Error(INVALID_FORMULA_MESSAGE),
                          );
                        }
                      }
                    }

                    if (value?.includes(DATE_FUNCTION_TYPES.ADD_DAYS)) {
                      const addDaysRegex =
                        regexPatterns?.functionWithParamsCapture(
                          DATE_FUNCTION_TYPES.ADD_DAYS,
                        );
                      const matches = [...value.matchAll(addDaysRegex)];

                      if (matches?.length > 0) {
                        const params = matches?.[0]?.[1]
                          ?.split(',')
                          ?.map((p) => p?.trim());
                        if (params?.length !== 2) {
                          return Promise.reject(
                            new Error(INVALID_FORMULA_MESSAGE),
                          );
                        }

                        const firstParamField = params?.[0]?.replace(/"/g, '');
                        const isFirstParamDateField = dateFields?.some(
                          (field) => field?.name === firstParamField,
                        );

                        const secondParam = params?.[1]?.replace(/"/g, '');
                        const isSecondParamPositiveNumber =
                          !Number.isNaN(Number(secondParam)) &&
                          Number(secondParam) > 0 &&
                          secondParam.length <= 4;

                        if (!isFirstParamDateField) {
                          return Promise.reject(
                            new Error(INVALID_FORMULA_MESSAGE),
                          );
                        }

                        if (!isSecondParamPositiveNumber) {
                          return Promise.reject(
                            new Error(INVALID_FORMULA_MESSAGE),
                          );
                        }
                      }
                    }

                    if (value?.includes(DATE_FUNCTION_TYPES.SUBTRACT_DAYS)) {
                      const subtractDaysRegex =
                        regexPatterns?.functionWithParamsCapture(
                          DATE_FUNCTION_TYPES.SUBTRACT_DAYS,
                        );
                      const matches = [...value.matchAll(subtractDaysRegex)];

                      if (matches?.length > 0) {
                        const params = matches?.[0]?.[1]
                          ?.split(',')
                          ?.map((p) => p?.trim());
                        if (params?.length !== 2) {
                          return Promise.reject(
                            new Error(INVALID_FORMULA_MESSAGE),
                          );
                        }

                        const firstParamField = params?.[0]?.replace(/"/g, '');
                        const isFirstParamDateField = dateFields?.some(
                          (field) => field?.name === firstParamField,
                        );

                        const secondParam = params?.[1]?.replace(/"/g, '');
                        const isSecondParamPositiveNumber =
                          !Number.isNaN(Number(secondParam)) &&
                          Number(secondParam) > 0 &&
                          secondParam.length <= 4;

                        if (!isFirstParamDateField) {
                          return Promise.reject(
                            new Error(INVALID_FORMULA_MESSAGE),
                          );
                        }

                        if (!isSecondParamPositiveNumber) {
                          return Promise.reject(
                            new Error(INVALID_FORMULA_MESSAGE),
                          );
                        }
                      }
                    }

                    if (value?.includes(TIME_FUNCTION_TYPES.TIME_SUBTRACTION)) {
                      const timeSubtractionRegex =
                        regexPatterns?.functionWithParamsCapture(
                          TIME_FUNCTION_TYPES.TIME_SUBTRACTION,
                        );
                      const matches = [...value.matchAll(timeSubtractionRegex)];

                      if (matches?.length > 0) {
                        const params = matches?.[0]?.[1]
                          ?.split(',')
                          ?.map((p) => p?.trim());
                        if (params?.length !== 2) {
                          return Promise.reject(
                            new Error(INVALID_FORMULA_MESSAGE),
                          );
                        }

                        const timeFieldParams = params?.filter((param) => {
                          const fieldName = param?.replace(/"/g, '');
                          return timeFields?.some(
                            (field) => field?.name === fieldName,
                          );
                        });

                        if (timeFieldParams?.length !== 2) {
                          return Promise.reject(
                            new Error(INVALID_FORMULA_MESSAGE),
                          );
                        }
                      }
                    }

                    const mathFuncError = mathematicalFunctions?.reduce(
                      (error, mathFunc) => {
                        if (error) return error;

                        if (value?.includes(mathFunc)) {
                          const paramsString = extractMathFunctionParams(
                            value,
                            mathFunc,
                          );

                          if (paramsString !== null) {
                            if (paramsString?.trim() === '') {
                              return INVALID_FORMULA_MESSAGE;
                            }

                            const params = paramsString?.match(
                              REGEX.QUOTED_TEXT_PATTERN,
                            );

                            if (!params || params?.length < 2) {
                              return INVALID_FORMULA_MESSAGE;
                            }

                            const commaCount = (paramsString?.match(/,/g) || [])
                              ?.length;
                            if (commaCount !== params?.length - 1) {
                              return INVALID_FORMULA_MESSAGE;
                            }

                            const cleanedParams = params?.map((param) =>
                              param?.replace(/"/g, ''),
                            );

                            const validFieldParams = cleanedParams?.filter(
                              (fieldName) => {
                                return numericFields?.some(
                                  (field) => field?.name === fieldName,
                                );
                              },
                            );

                            if (validFieldParams?.length !== params?.length) {
                              return INVALID_FORMULA_MESSAGE;
                            }
                          }
                        }
                        return null;
                      },
                      null,
                    );

                    if (mathFuncError) {
                      return Promise.reject(new Error(mathFuncError));
                    }

                    DATE_FUNCTIONS?.forEach((func) => {
                      const funcRegex = regexPatterns?.functionWithParams(func);
                      if (func === DATE_FUNCTION_TYPES.DAYS_BETWEEN) {
                        formulaToValidate = formulaToValidate?.replace(
                          funcRegex,
                          '1',
                        );
                      } else if (
                        func === DATE_FUNCTION_TYPES.ADD_DAYS ||
                        func === DATE_FUNCTION_TYPES.SUBTRACT_DAYS
                      ) {
                        const testDate = moment()?.format(DEFAULTDATEFORMAT);
                        formulaToValidate = formulaToValidate?.replace(
                          funcRegex,
                          `"${testDate}"`,
                        );
                      }
                    });

                    TIME_FUNCTIONS?.forEach((func) => {
                      const currentTime = moment().format(TIME_24_HOUR_FORMAT);
                      const funcRegex = regexPatterns?.functionWithParams(func);
                      if (func === TIME_FUNCTION_TYPES.TIME_SUBTRACTION) {
                        formulaToValidate = formulaToValidate?.replace(
                          funcRegex,
                          `"${currentTime}"`,
                        );
                      }
                    });

                    const formulaWithTestValues = formulaToValidate?.replace(
                      /"[^"]+"/g,
                      '1',
                    );

                    evaluate(formulaWithTestValues);
                    return Promise.resolve();
                  } catch (error) {
                    return Promise.reject(new Error(INVALID_FORMULA_MESSAGE));
                  }
                },
              }),
            ]}
          >
            <Input
              id="formula-input"
              ref={inputRef}
              placeholder="Enter Value"
              allowClear
              onChange={(e) => {
                const newValue = e?.target?.value;
                if (showTypeView === FIELD_TYPES_LABELS.DATE && newValue) {
                  const addDaysMatches = [
                    ...newValue?.matchAll(regexPatterns?.addDaysValidation()),
                  ];
                  const subtractDaysMatches = [
                    ...newValue?.matchAll(
                      regexPatterns?.subtractDaysValidation(),
                    ),
                  ];
                  let shouldPreventUpdate = false;
                  addDaysMatches?.forEach((match) => {
                    if (match?.[1] && match?.[1]?.length > 4) {
                      shouldPreventUpdate = true;
                    }
                  });
                  subtractDaysMatches?.forEach((match) => {
                    if (match?.[1] && match?.[1]?.length > 4) {
                      shouldPreventUpdate = true;
                    }
                  });
                  if (shouldPreventUpdate) {
                    return;
                  }
                }
                setFormulaFieldValue((prev) => ({
                  ...prev,
                  [showTypeView]: newValue,
                }));
              }}
              onKeyDown={(e) => {
                if (
                  (showTypeView === FIELD_TYPES_LABELS.DATE &&
                    e?.key === '.') ||
                  REGEX.ALPHABETS?.test(e?.key)
                ) {
                  e?.preventDefault();
                }
              }}
              disabled={
                (numericFields?.length === 0 &&
                  dateFields?.length === 0 &&
                  timeFields?.length === 0) ||
                isDisabled
              }
            />
          </Form.Item>
          {showTypeView === FIELD_TYPES_LABELS.NUMERIC && (
            <>
              <div>
                <strong className="ml-5">Example:</strong>
                {currentExamples[FIELD_TYPES_LABELS.NUMERIC]}
              </div>
              <h4 className="mb-10 mt-20">Numeric Fields</h4>
              {numericFields?.length > 0 ? (
                <div className="d-flex mb-10 flex-wrap">
                  {numericFields?.map((field, index) => (
                    <div key={field?.id} className="d-flex flex-wrap">
                      <Button
                        id={`numeric-field-btn-${index}`}
                        shape="round"
                        className="cancel-button mr-10 mb-10"
                        disabled={isDisabled}
                        onClick={() => handleButtonClick(field)}
                      >
                        {field?.name}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-danger note-text mt-10 mb-10">
                  Note: Please add required numeric fields to set the formula.
                </div>
              )}

              <h4>Operators</h4>
              <div className="d-flex mb-10">
                {OPERATORS?.map((operator, index) => (
                  <Button
                    id={`operator-btn-${index}`}
                    key={operator}
                    shape="round"
                    className="d-flex align-center save-button mr-10"
                    onClick={() => handleOperatorClick(operator)}
                    disabled={numericFields?.length === 0 || isDisabled}
                  >
                    {operator}
                  </Button>
                ))}
              </div>

              <h4 className="d-flex align-center">
                Mathematical Functions
                <CommonPopover
                  title="Mathematical Functions Help"
                  classNames={{ root: 'math-function-popover' }}
                  content={
                    <div>
                      <div>
                        Mathematical functions are used to perform mathematical
                        operations on the fields. Here are some common
                        mathematical functions available:
                      </div>
                      <ul>
                        <li>
                          <b>mean("a", "b", ...):</b> Calculates the average.
                        </li>
                        <li>
                          <b>median("a", "b", ...):</b> Finds the middle value.
                        </li>
                        <li>
                          <b>std("a", "b", ...):</b> Computes the standard
                          deviation.
                        </li>
                      </ul>
                    </div>
                  }
                >
                  <InfoIconDark
                    height={16}
                    width={16}
                    className="ml-5 rotate-180"
                  />
                </CommonPopover>
              </h4>
              <div className="d-flex mb-10">
                {mathematicalFunctions?.map((operator, index) => (
                  <Button
                    id={`math-function-btn-${index}`}
                    key={operator}
                    shape="round"
                    className="d-flex align-center save-button mr-10"
                    onClick={() => handleOperatorClick(operator)}
                    disabled={numericFields?.length === 0 || isDisabled}
                  >
                    {titleCase(operator)}
                  </Button>
                ))}
              </div>
            </>
          )}
          {showTypeView === FIELD_TYPES_LABELS.DATE && (
            <>
              <div>
                <strong className="ml-5">Example:</strong>
                {currentExamples[FIELD_TYPES_LABELS.DATE]}
              </div>
              <h4 className="mb-10 mt-20">Date Fields</h4>
              {dateFields?.length > 0 ? (
                <div className="d-flex mb-10 flex-wrap">
                  {dateFields?.map((field, index) => (
                    <div key={field?.id} className="d-flex flex-wrap">
                      <Button
                        id={`date-field-btn-${index}`}
                        shape="round"
                        className="primary-button mr-10 mb-10"
                        disabled={
                          isDisabled ||
                          !hasFunctionSelected() ||
                          isDateTimeFormulaCompleted()
                        }
                        onClick={() => handleButtonClick(field)}
                      >
                        {field?.name}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-danger note-text mt-10 mb-10">
                  Note: Please add required date fields to set the formula.
                </div>
              )}
              <h4 className="d-flex align-center">
                Date Functions
                <CommonPopover
                  title="Date Functions Help"
                  classNames={{ root: 'math-function-popover' }}
                  content={
                    <div>
                      <div>
                        Date functions are used to perform date calculations
                        between date fields.
                      </div>
                      <ul>
                        <li>
                          <b>daysBetween("date1", "date2"):</b> Calculates the
                          number of days between two dates.
                        </li>
                        <li>
                          <b>addDays("date", days):</b> Adds specified days to a
                          date.
                        </li>
                        <li>
                          <b>subtractDays("date", days):</b> Subtracts specified
                          days from a date.
                        </li>
                      </ul>
                    </div>
                  }
                >
                  <InfoIconDark
                    height={16}
                    width={16}
                    className="ml-5 rotate-180"
                  />
                </CommonPopover>
              </h4>
              <div className="d-flex mb-10">
                {DATE_FUNCTIONS?.map((operator, index) => (
                  <Button
                    id={`date-function-btn-${index}`}
                    key={operator}
                    shape="round"
                    className="d-flex align-center save-button mr-10"
                    onClick={() => handleOperatorClick(`${operator}`)}
                    disabled={
                      dateFields?.length === 0 ||
                      isDisabled ||
                      isDateTimeFormulaCompleted()
                    }
                  >
                    {titleCase(operator)}
                  </Button>
                ))}
              </div>
            </>
          )}
          {showTypeView === FIELD_TYPES_LABELS.TIME && (
            <>
              <div>
                <strong className="ml-5">Example:</strong>
                {currentExamples[FIELD_TYPES_LABELS.TIME]}
              </div>
              <h4 className="mb-10 mt-20">Time Fields</h4>
              {timeFields?.length > 0 ? (
                <div className="d-flex mb-10 flex-wrap">
                  {timeFields?.map((field, index) => (
                    <div key={field?.id} className="d-flex flex-wrap">
                      <Button
                        id={`time-field-btn-${index}`}
                        shape="round"
                        className="primary-button mr-10 mb-10"
                        disabled={
                          isDisabled ||
                          !hasFunctionSelected() ||
                          isDateTimeFormulaCompleted()
                        }
                        onClick={() => handleButtonClick(field)}
                      >
                        {field?.name}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-danger note-text mt-10 mb-10">
                  Note: Please add required time fields to set the formula.
                </div>
              )}

              <h4 className="d-flex align-center">
                Time Functions
                <CommonPopover
                  title="Time Functions Help"
                  classNames={{ root: 'math-function-popover' }}
                  content={
                    <div>
                      <div>
                        Time functions are used to perform time calculations
                        between time fields.
                      </div>
                      <ul>
                        <li>
                          <b>timeSubtraction("time1", "time2"):</b> Subtracts
                          time2 from time1 and returns the difference in
                          minutes.
                        </li>
                      </ul>
                    </div>
                  }
                >
                  <InfoIconDark
                    height={16}
                    width={16}
                    className="ml-5 rotate-180"
                  />
                </CommonPopover>
              </h4>
              <div className="d-flex mb-10">
                {TIME_FUNCTIONS?.map((operator, index) => (
                  <Button
                    id={`time-function-btn-${index}`}
                    key={operator}
                    shape="round"
                    className="d-flex align-center save-button mr-10"
                    onClick={() => handleOperatorClick(`${operator}`)}
                    disabled={
                      timeFields?.length === 0 ||
                      isDisabled ||
                      isDateTimeFormulaCompleted()
                    }
                  >
                    {titleCase(operator)}
                  </Button>
                ))}
              </div>
            </>
          )}
        </Col>
      </Row>
    </>
  );
};

const AddFieldModal = ({
  showModal,
  setShowModal,
  numericFields,
  setNumericFields,
  tableHeaders,
  isDisabled = false,
  selectedField,
  isUpdate,
  setSelectedField,
  handleRefetch,
}) => {
  const {
    params: { registerId },
  } = useRouter();
  const { state, dispatch } = useContext(AppContext);
  const inputRef = useRef(null);
  const [form] = Form.useForm();
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const [displayOption, setDisplayOption] = useState(false);
  const [isOptionType, setIsOptionType] = useState(false);
  const [dateFields, setDateFields] = useState([]);
  const [timeFields, setTimeFields] = useState([]);
  const [showTypeView, setShowTypeView] = useState(FIELD_TYPES_LABELS.NUMERIC);
  const [formulaFieldValue, setFormulaFieldValue] = useState({
    [FIELD_TYPES_LABELS.NUMERIC]: '',
    [FIELD_TYPES_LABELS.DATE]: '',
    [FIELD_TYPES_LABELS.TIME]: '',
  });
  const optionsInputRef = useRef(null);

  const showRouterPrompt = (value) => {
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: value,
    });
  };

  useEffect(() => {
    let filteredNumericFields = [];
    let filteredDateFields = [];
    let filteredTimeFields = [];

    const getFilteredFields = (types, calcType) => {
      return tableHeaders?.filter((field) => {
        const isMatch =
          types.includes(field?.type) ||
          (field?.type === FIELD_TYPES.CALCULATION &&
            field?.calculationResultType === calcType);

        return selectedField?.id
          ? isMatch && field?.id !== selectedField?.id
          : isMatch;
      });
    };

    filteredNumericFields = getFilteredFields(
      [FIELD_TYPES.NUMERIC],
      REGISTER_CALCULATION_TYPES.NUMERIC,
    );

    filteredDateFields = getFilteredFields(
      [FIELD_TYPES.DATE],
      REGISTER_CALCULATION_TYPES.DATE,
    );

    filteredTimeFields = getFilteredFields([FIELD_TYPES.TIME]);

    setNumericFields(filteredNumericFields);
    setDateFields(filteredDateFields);
    setTimeFields(filteredTimeFields);
  }, [tableHeaders]);

  const selectionType = useWatch('type', form);
  const [createRegisterFormField, { loading: createLoading }] = useMutation(
    CREATE_REGISTER_FORM_FIELD,
    {
      onError() {},
      onCompleted: () => {
        setShowModal(false);
        handleRefetch();
      },
    },
  );
  const [updateRegisterFormField, { loading: updateLoading }] = useMutation(
    UPDATE_REGISTER_FORM_FIELD,
    {
      onError() {},
      onCompleted: () => {
        setShowModal(false);
        handleRefetch();
      },
    },
  );

  const onFinish = () => {
    const formValues = form.getFieldsValue();

    if (formValues?.type === FIELD_TYPES.CALCULATION) {
      const optionArr = [];
      if (registerId) {
        const formattedFormula = formValues?.formula?.replace(
          /"([^"]+)"/g,
          (match) => {
            const numericField = filter(
              numericFields,
              (record) => match === `"${record?.name}"`,
            )?.[0];
            if (numericField) {
              return numericField?.id;
            }
            const dateField = filter(
              dateFields,
              (record) => match === `"${record?.name}"`,
            )?.[0];
            if (dateField) {
              return dateField.id;
            }
            const timeField = filter(
              timeFields,
              (record) => match === `"${record?.name}"`,
            )?.[0];
            if (timeField) {
              return timeField.id;
            }
            return match;
          },
        );
        formValues.options = [formattedFormula];
        const formulaValue = formattedFormula
          ?.replace(/\\"/g, '')
          ?.replace(/"/g, '');
        optionArr?.push(formulaValue);
      } else {
        formValues.options = [formValues?.formula];
        const formulaValue = formValues?.formula
          ?.replace(/\\"/g, '')
          ?.replace(/"/g, '');
        optionArr?.push(formulaValue);
      }
      formValues.options = optionArr;

      const formula = formValues?.options[0] || '';
      if (formula?.includes(DATE_FUNCTION_TYPES.DAYS_BETWEEN)) {
        formValues.calculationResultType = REGISTER_CALCULATION_TYPES.NUMERIC;
      } else if (
        DATE_ARITHMETIC_FUNCTIONS.some((func) => formula?.includes(func))
      ) {
        formValues.calculationResultType = REGISTER_CALCULATION_TYPES.DATE;
      } else if (formula?.includes(TIME_FUNCTION_TYPES.TIME_SUBTRACTION)) {
        formValues.calculationResultType = REGISTER_CALCULATION_TYPES.NUMERIC;
      } else {
        formValues.calculationResultType = REGISTER_CALCULATION_TYPES.NUMERIC;
      }

      delete formValues?.formula;

      if (
        formValues?.options[0]?.includes(TIME_FUNCTION_TYPES.TIME_SUBTRACTION)
      ) {
        if (!formValues?.name?.includes('(hrs.)')) {
          formValues.name = `${formValues?.name} (hrs.)`;
        }
      }
      if (formValues?.options[0]?.includes(DATE_FUNCTION_TYPES.DAYS_BETWEEN)) {
        if (!formValues?.name?.includes('(days)')) {
          formValues.name = `${formValues?.name} (days)`;
        }
      }
    }
    if (
      formValues?.options?.length > 0 &&
      formValues?.type !== FIELD_TYPES.CALCULATION
    ) {
      formValues.options = formValues?.options?.map((value) => value?.option);
    }
    if (isUpdate) {
      updateRegisterFormField({
        variables: {
          updateRegisterFormFieldId: selectedField?.id,
          data: {
            name: formValues?.name,
            isRequired: formValues?.isRequired,
            type: formValues?.type,
            options: formValues?.options || null,
            calculationResultType: formValues?.calculationResultType || null,
            sequence: selectedField?.sequence,
          },
        },
      });
      showRouterPrompt(false);
    } else {
      createRegisterFormField({
        variables: {
          data: {
            registerId,
            ...formValues,
            options: formValues?.options || null,
            sequence:
              tableHeaders?.[tableHeaders?.length - 1]?.sequence + 1 || 1,
          },
        },
      });
      showRouterPrompt(false);
    }
  };

  const handleTypeChange = (options) => {
    switch (options?.value) {
      case FIELD_TYPES.OPTIONS:
        setDisplayOption(true);
        setIsOptionType(true);
        break;

      case FIELD_TYPES.CHECKBOXES:
        setDisplayOption(true);
        setIsOptionType(true);
        break;

      case FIELD_TYPES.CALCULATION:
        setDisplayOption(true);
        setIsOptionType(false);
        break;

      default:
        setDisplayOption(false);
        setIsOptionType(false);
        break;
    }
  };

  useEffect(() => {
    if (isUpdate && selectedField) {
      handleTypeChange({ value: selectedField?.type });
    }
  }, [showModal, selectedField, numericFields, dateFields]);

  const handleSelect = (labeledValue, options) => {
    handleTypeChange(options);
    const existingOptions = form?.getFieldValue('options');
    if (labeledValue === FIELD_TYPES.OPTIONS) {
      form.setFieldsValue({
        options: existingOptions?.length ? existingOptions : [{}, {}],
      });
    } else if (labeledValue === FIELD_TYPES.CHECKBOXES) {
      form.setFieldsValue({
        options: existingOptions?.length ? existingOptions : [{}, {}],
      });
    }
  };

  useEffect(() => {
    if (isUpdate && showModal && selectedField) {
      const formattedOptions =
        selectedField?.type !== FIELD_TYPES.CALCULATION
          ? selectedField?.options?.map((option) => ({
              option,
            }))
          : [];
      if (selectedField?.type === FIELD_TYPES.CALCULATION) {
        const formattedFormula =
          selectedField?.type === FIELD_TYPES.CALCULATION
            ? selectedField?.options[0]
            : [];
        const replacedFormula = formattedFormula?.replace(
          REGEX.UUID,
          (match) => {
            const numericField = filter(
              numericFields,
              (record) => match === record?.id,
            )?.[0];
            if (numericField) {
              return `"${numericField?.name}"`;
            }
            const dateField = filter(
              dateFields,
              (record) => match === record?.id,
            )?.[0];
            if (dateField) {
              return `"${dateField?.name}"`;
            }
            const timeField = filter(
              timeFields,
              (record) => match === record?.id,
            )?.[0];
            if (timeField) {
              return `"${timeField?.name}"`;
            }
            return match;
          },
        );
        form.setFieldsValue({ formula: replacedFormula });

        if (replacedFormula) {
          const hasDateFunctions = DATE_FUNCTIONS?.some((func) =>
            replacedFormula?.includes(func),
          );
          const hasTimeFunctions = TIME_FUNCTIONS?.some((func) =>
            replacedFormula?.includes(func),
          );
          if (hasTimeFunctions) {
            setShowTypeView(FIELD_TYPES_LABELS.TIME);
          } else if (hasDateFunctions) {
            setShowTypeView(FIELD_TYPES_LABELS.DATE);
          } else {
            setShowTypeView(FIELD_TYPES_LABELS.NUMERIC);
          }
        }
      }

      const transformedValues = {
        ...selectedField,
        options: selectedField?.options?.map((option) => ({ option })) || null,
      };
      form.setFieldsValue({
        ...selectedField,
        options: formattedOptions,
      });
      form.setFieldsValue(transformedValues);
    }
  }, [showModal, selectedField, isUpdate, numericFields, dateFields]);

  return (
    <div>
      <RouterPrompt openPrompt={state?.showPrompt} />
      <Modal
        open={showModal}
        footer={false}
        centered
        className="register-table-modal"
        maskClosable={false}
        closable={false}
        form={form}
        destroyOnHidden
      >
        <h2 className="mb-15">{isUpdate ? 'Edit ' : 'Add '} Field</h2>
        <Form
          layout="vertical"
          form={form}
          initialValues={{
            type: 'TEXT',
            isRequired: true,
            options: [{}, {}],
          }}
          onFieldsChange={() => {
            showRouterPrompt(true);
          }}
          className="mt-16"
          onFinish={onFinish}
        >
          <Form.Item
            rules={[
              required,
              characterWithoutWhiteSpace,
              { max: 250, message: 'Maximum 250 characters are allowed' },
            ]}
            name="name"
            label="Property Name"
          >
            <Input
              id="field-name-input"
              placeholder="Enter property name"
              disabled={isDisabled}
              allowClear
            />
          </Form.Item>
          <Row gutter={30}>
            <Col span={isDesktopViewport ? 18 : 24}>
              <Form.Item name="type" label="Type">
                <CommonSelect
                  id="field-type-select"
                  className="mr-3"
                  placeholder="Select Type"
                  onSelect={handleSelect}
                  options={[
                    ...keys(FIELD_TYPES)
                      ?.filter(
                        (type) =>
                          type !== FIELD_TYPES?.NOMENCLATURE &&
                          type !== FIELD_TYPES?.EQC,
                      )
                      ?.map((type) => {
                        return {
                          key: FIELD_TYPES_LABELS[type],
                          value: type,
                          label: FIELD_TYPES_LABELS[type],
                        };
                      }),
                  ]}
                  disabled={isDisabled}
                />
              </Form.Item>
            </Col>
            <Col span={isDesktopViewport ? 6 : 24}>
              <Form.Item
                name="isRequired"
                label="Required"
                valuePropName="checked"
              >
                <Switch
                  id="field-required-switch"
                  defaultChecked
                  disabled={isDisabled}
                />
              </Form.Item>
            </Col>
          </Row>
          {selectionType === FIELD_TYPES?.CALCULATION && (
            <div className="text-danger note-text">
              Note: All related field will be made mandatory
            </div>
          )}
          {displayOption && (
            <>
              <Divider dashed />
              {isOptionType ? (
                <>
                  <h3>Options</h3>
                  <CommonRegisterOptionUi
                    isDisabled={isDisabled}
                    form={form}
                    optionsInputRef={optionsInputRef}
                  />
                </>
              ) : (
                <h3>Calculation</h3>
              )}

              <div className="mt-10">
                {!isOptionType && (
                  <CalculationLogicUi
                    isDisabled={isDisabled}
                    numericFields={numericFields}
                    dateFields={dateFields}
                    timeFields={timeFields}
                    form={form}
                    inputRef={inputRef}
                    showTypeView={showTypeView}
                    setShowTypeView={setShowTypeView}
                    formulaFieldValue={formulaFieldValue}
                    setFormulaFieldValue={setFormulaFieldValue}
                    type="fieldCalculation"
                    mathematicalFunctions={MATHEMATICAL_FUNCTIONS}
                  />
                )}
              </div>
            </>
          )}
          <div className="form-buttons">
            <Button
              id="add-field-cancel-btn"
              shape="round"
              className="cancel-button"
              disabled={createLoading || updateLoading}
              onClick={() => {
                setShowModal(false);
                setSelectedField();
                showRouterPrompt(false);
              }}
            >
              Cancel
            </Button>
            <CanPerform
              action={ALLOWED_ACTION_KEYS.ADD_PROJECT_REGISTER}
              type={ALLOWED_ACTION_TYPE.BOTH}
            >
              {!isDisabled && (
                <Button
                  id="add-field-save-btn"
                  shape="round"
                  type="primary"
                  className="save-button"
                  htmlType="submit"
                  loading={createLoading || updateLoading}
                >
                  Save
                </Button>
              )}
            </CanPerform>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AddFieldModal;
